import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  getUserTeams,
  getInvoicesForReport,
  formatCurrency,
  getCurrentFactorValue,
} from "../services/firebaseService";
import { DateRange, Invoice, InvoiceService, Team } from "../types";
import "./ByTypeReport.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

type TeamRow = {
  teamId: string;
  teamName: string;
  fatTotal: number;
  deslocamento: number;
  alojamento: number;
  prodOuDispon: number;
};

const normalize = (s?: string) => (s || "").toString().toUpperCase();

const serviceValueWithFactor = (s: InvoiceService): number => {
  const price = parseFloat((s?.price as any)?.toString?.() || "0");
  const qty = parseFloat((s?.quantity as any)?.toString?.() || "0");
  const rawFactor = parseFloat((s?.factor as any)?.toString?.() || "");
  const factor = !isNaN(rawFactor) && rawFactor > 0 ? rawFactor : getCurrentFactorValue();
  return price * qty * factor;
};

const getServiceKind = (s: InvoiceService): "DESLOCAMENTO" | "ALOJAMENTO" | "OUTROS" => {
  const code = normalize((s as any)?.code || "").replace(/\s+/g, "");
  // Alojamento: codes ending 120SI or 120SC
  if (code.endsWith("120SI") || code.endsWith("120SC")) return "ALOJAMENTO";
  // Deslocamento: codes ending 108SC or 119SI
  if (code.endsWith("108SC") || code.endsWith("119SI")) return "DESLOCAMENTO";
  return "OUTROS";
};

const ByTypeReport: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({ startDate: "", endDate: "" });

  const loadUserTeams = async () => {
    try {
      const userTeams = await getUserTeams(user!.uid);
      setTeams(userTeams);
      setSelectedTeams([]);
    } catch (error) {
      console.error("Error loading teams:", error);
    }
  };

  useEffect(() => {
    if (user) {
      loadUserTeams();
      setInitialDateRange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setInitialDateRange = () => {
    const currentDay = new Date().getDate();
    const currentDate = new Date();
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
    const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1);

    let startDate: string, endDate: string;
    if (currentDay < 26) {
      startDate = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, "0")}-26`;
      endDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-25`;
    } else {
      startDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-26`;
      endDate = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, "0")}-25`;
    }
    setDateRange({ startDate, endDate });
  };

  const loadReportData = async () => {
    if (selectedTeams.length === 0 || !dateRange.startDate || !dateRange.endDate) {
      alert("Selecione pelo menos uma equipe e defina o período.");
      return;
    }
    setLoading(true);
    try {
      const allInvoices: Invoice[] = [];
      for (const teamId of selectedTeams) {
        const teamInvoices = await getInvoicesForReport(teamId, dateRange.startDate, dateRange.endDate);
        allInvoices.push(...teamInvoices);
      }
      setInvoices(allInvoices);
    } catch (error) {
      console.error("Error loading report data:", error);
      alert("Erro ao carregar dados do relatório.");
    } finally {
      setLoading(false);
    }
  };

  const rows: TeamRow[] = useMemo(() => {
    const selectedSet = new Set(selectedTeams);
    const byTeam = new Map<string, TeamRow>();

    for (const inv of invoices) {
      const t = inv.team || "";
      if (!byTeam.has(t)) {
        byTeam.set(t, {
          teamId: t,
          teamName: t,
          fatTotal: 0,
          deslocamento: 0,
          alojamento: 0,
          prodOuDispon: 0,
        });
      }
      const agg = byTeam.get(t)!;
      // Fat. Total by invoice
      agg.fatTotal += inv.totalWithFactor || inv.total || 0;
      // Category sums by services
      const services = Array.isArray(inv.services) ? inv.services : [];
      for (const s of services) {
        const val = serviceValueWithFactor(s as InvoiceService);
        const kind = getServiceKind(s as InvoiceService);
        if (kind === "DESLOCAMENTO") agg.deslocamento += val;
        else if (kind === "ALOJAMENTO") agg.alojamento += val;
        else agg.prodOuDispon += val;
      }
    }

    // Only include selected teams; keep zero rows for teams without invoices
    const out: TeamRow[] = [];
    for (const t of teams) {
      if (!selectedSet.has(t.id)) continue;
      const base = byTeam.get(t.id) || {
        teamId: t.id,
        teamName: t.name,
        fatTotal: 0,
        deslocamento: 0,
        alojamento: 0,
        prodOuDispon: 0,
      };
      // Ensure displayed name from teams list
      base.teamName = t.name;
      out.push(base);
    }

    // Stable order by team id
    out.sort((a, b) => a.teamId.localeCompare(b.teamId));
    return out;
  }, [invoices, teams, selectedTeams]);

  const exportToCSV = () => {
    const headers = [
      "Equipe",
      "Fat. Total",
      "Deslocamento",
      "Alojamento",
      "Produção ou Disponibilidade",
    ].join(";");

    const lines = rows.map((r) =>
      [
        `"${r.teamName}"`,
        r.fatTotal.toFixed(2),
        r.deslocamento.toFixed(2),
        r.alojamento.toFixed(2),
        r.prodOuDispon.toFixed(2),
      ].join(";")
    );

    const csvContent = [headers, ...lines].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `relatorio-por-tipo-${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeams((prev) =>
      prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId]
    );
  };

  const handleSelectAllTeams = () => setSelectedTeams(teams.map((t) => t.id));
  const handleDeselectAllTeams = () => setSelectedTeams([]);

  const insights = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    const topBy = <K extends keyof TeamRow>(key: K) => {
      let best: TeamRow | null = null;
      for (const r of rows) {
        if (!best || (r[key] as unknown as number) > (best[key] as unknown as number)) {
          best = r;
        }
      }
      return best ? { team: best.teamName, value: best[key] as unknown as number } : { team: "—", value: 0 };
    };
    return {
      aloj: topBy('alojamento'),
      desloc: topBy('deslocamento'),
      prod: topBy('prodOuDispon'),
    };
  }, [rows]);

  return (
    <div className="bytype-report-container">
      <div className="report-header">
        <div className="header-content">
          <h1>Relatório por Tipo</h1>
          <button onClick={() => window.close()} className="close-btn">
            <i className="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>

      <div className="report-content">
        <div className="filters-section">
          <div className="filter-group">
            <div className="teams-header">
              <h3>Equipes</h3>
              <div className="team-controls">
                <button onClick={handleSelectAllTeams} className="control-btn">Selecionar Todas</button>
                <button onClick={handleDeselectAllTeams} className="control-btn">Desmarcar Todas</button>
              </div>
            </div>
            <div className="teams-grid">
              {teams.map((team) => (
                <label key={team.id} className="team-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedTeams.includes(team.id)}
                    onChange={() => handleTeamToggle(team.id)}
                  />
                  {team.name}
                </label>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h3>Período</h3>
            <div className="date-inputs">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange((p) => ({ ...p, startDate: e.target.value }))}
                className="date-input"
              />
              <span>até</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange((p) => ({ ...p, endDate: e.target.value }))}
                className="date-input"
              />
            </div>
          </div>

          <div className="action-buttons">
            <button
              onClick={loadReportData}
              disabled={loading || selectedTeams.length === 0}
              className="load-btn"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Carregando...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i>
                  Gerar Relatório
                </>
              )}
            </button>
            {invoices.length > 0 && (
              <button onClick={exportToCSV} className="export-btn">
                <i className="fas fa-download"></i>
                Exportar CSV
              </button>
            )}
          </div>
        </div>

        {invoices.length > 0 && (
          <div className="results-section">
            <div className="results-header">
              <h3>Resumo por Equipe</h3>
            </div>
            <div className="table-wrapper">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Equipe</th>
                    <th>Fat. Total</th>
                    <th>Deslocamento</th>
                    <th>Alojamento</th>
                    <th>Produção ou Disponibilidade</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.teamId}>
                      <td>{row.teamName}</td>
                      <td className="value-cell">{formatCurrency(row.fatTotal)}</td>
                      <td className="value-cell">{formatCurrency(row.deslocamento)}</td>
                      <td className="value-cell">{formatCurrency(row.alojamento)}</td>
                      <td className="value-cell">{formatCurrency(row.prodOuDispon)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ fontWeight: "bold" }}>Total</td>
                    <td className="value-cell" style={{ fontWeight: "bold" }}>{formatCurrency(rows.reduce((s, r) => s + r.fatTotal, 0))}</td>
                    <td className="value-cell" style={{ fontWeight: "bold" }}>{formatCurrency(rows.reduce((s, r) => s + r.deslocamento, 0))}</td>
                    <td className="value-cell" style={{ fontWeight: "bold" }}>{formatCurrency(rows.reduce((s, r) => s + r.alojamento, 0))}</td>
                    <td className="value-cell" style={{ fontWeight: "bold" }}>{formatCurrency(rows.reduce((s, r) => s + r.prodOuDispon, 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {rows.length > 0 && insights && (
          <div className="summary-section">
            <h3>Insights</h3>
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-value">{formatCurrency(insights.aloj.value)}</div>
                <div className="card-label">Maior Alojamento — {insights.aloj.team}</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{formatCurrency(insights.desloc.value)}</div>
                <div className="card-label">Maior Deslocamento — {insights.desloc.team}</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{formatCurrency(insights.prod.value)}</div>
                <div className="card-label">Maior Produção/Dispon. — {insights.prod.team}</div>
              </div>
            </div>
          </div>
        )}

        {rows.length > 0 && (
          <div className="charts-section">
            <h3>Gráficos por Equipe</h3>
            <div className="chart-grid">
              {rows.map((row) => {
                const totalCategorias = (row.alojamento || 0) + (row.deslocamento || 0) + (row.prodOuDispon || 0);
                const asPct = (v: number) => totalCategorias > 0 ? (v / totalCategorias) * 100 : 0;

                const data = {
                  labels: ['Alojamento', 'Deslocamento', 'Produção ou Disponibilidade'],
                  datasets: [
                    {
                      label: 'Percentual',
                      data: [asPct(row.alojamento), asPct(row.deslocamento), asPct(row.prodOuDispon)],
                      backgroundColor: ['#4C51BF', '#3182CE', '#38A169'],
                    },
                  ],
                };

                const options = {
                  responsive: true,
                  plugins: {
                    legend: { display: false },
                    title: { display: false },
                    tooltip: {
                      callbacks: {
                        label: (context: any) => {
                          const category = context.label || '';
                          const v = context.parsed?.y ?? context.raw ?? 0;
                          const pct = typeof v === 'number' ? v : Number(v);
                          return `${category}: ${pct.toFixed(1)}%`;
                        },
                      },
                    },
                  },
                  scales: {
                    x: { stacked: false },
                    y: {
                      stacked: false,
                      min: 0,
                      max: 100,
                      ticks: {
                        callback: (value: any) => `${value}%`,
                      },
                    },
                  },
                };

                return (
                  <div key={row.teamId} className="chart-card">
                    <div className="chart-card-header">
                      <span className="chart-team-name">{row.teamName}</span>
                      <span className="chart-total">Total: {formatCurrency(row.fatTotal)}</span>
                    </div>
                    <Bar data={data} options={options} />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {invoices.length === 0 && !loading && (
          <div className="no-data">
            <i className="fas fa-layer-group"></i>
            <h3>Nenhum dado carregado</h3>
            <p>Selecione equipes, período e clique em "Gerar Relatório".</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ByTypeReport;
